import React from "react";
import { Card, Col } from "react-bootstrap";
import {
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { BarChartFill } from "react-bootstrap-icons";
import { useDispatch, useSelector } from "react-redux";
import { handleClaimDashboardFilter } from "../config/claimDashboardSlice";

const DEFAULT_PAYER_COMPARISON_DATA = [
  {
    payer: "Bupa Arabia",
    code: "BUPA",
    claimsCount: 4850,
    grossAmount: 1350000,
    netSubmitted: 1200000,
    approvedAmount: 1050000,
    rejectedAmount: 150000,
    pendingAmount: 65000,
    approvalRate: 87.5,
    rejectionRate: 12.5,
    avgClaimValue: 278.3,
  },
  {
    payer: "Tawuniya",
    code: "TAW",
    claimsCount: 3920,
    grossAmount: 1080000,
    netSubmitted: 980000,
    approvedAmount: 890000,
    rejectedAmount: 90000,
    pendingAmount: 50000,
    approvalRate: 90.8,
    rejectionRate: 9.2,
    avgClaimValue: 275.5,
  },
  {
    payer: "MedGulf",
    code: "MEDG",
    claimsCount: 2450,
    grossAmount: 650000,
    netSubmitted: 580000,
    approvedAmount: 510000,
    rejectedAmount: 70000,
    pendingAmount: 35000,
    approvalRate: 87.9,
    rejectionRate: 12.1,
    avgClaimValue: 265.3,
  },
  {
    payer: "GIG Saudi",
    code: "GIG",
    claimsCount: 1840,
    grossAmount: 490000,
    netSubmitted: 430000,
    approvedAmount: 385000,
    rejectedAmount: 45000,
    pendingAmount: 28000,
    approvalRate: 89.5,
    rejectionRate: 10.5,
    avgClaimValue: 266.3,
  },
  {
    payer: "Malath Insurance",
    code: "MAL",
    claimsCount: 1280,
    grossAmount: 340000,
    netSubmitted: 300000,
    approvedAmount: 268000,
    rejectedAmount: 32000,
    pendingAmount: 22000,
    approvalRate: 89.3,
    rejectionRate: 10.7,
    avgClaimValue: 265.6,
  },
  {
    payer: "Al Rajhi Takaful",
    code: "RAJ",
    claimsCount: 1150,
    grossAmount: 310000,
    netSubmitted: 275000,
    approvedAmount: 248000,
    rejectedAmount: 27000,
    pendingAmount: 18000,
    approvalRate: 90.1,
    rejectionRate: 9.9,
    avgClaimValue: 269.5,
  },
  {
    payer: "Allianz SF",
    code: "ALZ",
    claimsCount: 950,
    grossAmount: 245000,
    netSubmitted: 215000,
    approvedAmount: 194000,
    rejectedAmount: 21000,
    pendingAmount: 14000,
    approvalRate: 90.2,
    rejectionRate: 9.8,
    avgClaimValue: 257.8,
  },
  {
    payer: "AXA Cooperative",
    code: "AXA",
    claimsCount: 820,
    grossAmount: 210000,
    netSubmitted: 185000,
    approvedAmount: 167000,
    rejectedAmount: 18000,
    pendingAmount: 12000,
    approvalRate: 90.2,
    rejectionRate: 9.8,
    avgClaimValue: 256.0,
  },
  {
    payer: "Walaa Insurance",
    code: "WLA",
    claimsCount: 710,
    grossAmount: 185200,
    netSubmitted: 162100,
    approvedAmount: 146000,
    rejectedAmount: 16100,
    pendingAmount: 10500,
    approvalRate: 90.1,
    rejectionRate: 9.9,
    avgClaimValue: 260.8,
  },
  {
    payer: "Arabian Shield",
    code: "ARS",
    claimsCount: 572,
    grossAmount: 140000,
    netSubmitted: 123000,
    approvedAmount: 107200,
    rejectedAmount: 15800,
    pendingAmount: 7500,
    approvalRate: 87.2,
    rejectionRate: 12.8,
    avgClaimValue: 244.8,
  },
];

const formatYAxis = (val) => {
  if (val >= 1000000) return `${(val / 1000000).toFixed(1)}M`;
  if (val >= 1000) return `${(val / 1000).toFixed(0)}k`;
  return val.toString();
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const fullPayerName = payload[0]?.payload?.payer || label;
    const payerCode =
      payload[0]?.payload?.code && payload[0]?.payload?.code !== fullPayerName
        ? ` (${payload[0].payload.code})`
        : "";

    return (
      <div
        className="p-3 rounded shadow-lg text-white"
        style={{
          backgroundColor: "#0F172A",
          border: "1px solid #334155",
          fontSize: "12px",
          minWidth: "250px",
          whiteSpace: "nowrap",
        }}
      >
        <div
          className="fw-bold border-bottom pb-1 mb-2"
          style={{ color: "#60A5FA", borderColor: "#334155" }}
        >
          Payer: {fullPayerName}
          {payerCode}
        </div>
        <div className="d-flex flex-column gap-1">
          {payload.map((p) => (
            <div
              key={p.name}
              className="d-flex justify-content-between align-items-center gap-3"
            >
              <span className="d-flex align-items-center gap-2 text-light">
                <span
                  style={{
                    width: "10px",
                    height: "10px",
                    borderRadius: "50%",
                    backgroundColor: p.color,
                    display: "inline-block",
                  }}
                ></span>
                {p.name}:
              </span>
              <span className="fw-bold text-white">
                SAR {Number(p.value).toLocaleString("en-US")}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return null;
};

const PayerComparisonChart = ({
  data = DEFAULT_PAYER_COMPARISON_DATA,
  selectedPayer,
}) => {
  const dispatch = useDispatch();
  const reduxActivePayer = useSelector(
    (state) => state?.claimDashboard?.claimDashboardFilters?.insurance || "All",
  );

  const currentActivePayer =
    selectedPayer !== undefined && selectedPayer !== "All"
      ? selectedPayer
      : reduxActivePayer;

  const displayData = React.useMemo(() => {
    if (!currentActivePayer || currentActivePayer === "All") {
      return data;
    }
    const filtered = data.filter(
      (item) =>
        item.payer === currentActivePayer || item.code === currentActivePayer,
    );
    return filtered.length > 0 ? filtered : data;
  }, [data, currentActivePayer]);

  const isFiltered = Boolean(
    currentActivePayer && currentActivePayer !== "All",
  );

  const handleClearFilter = () => {
    dispatch(
      handleClaimDashboardFilter({
        check: "insurance",
        value: null,
      }),
    );
  };

  return (
    <Col xs={12} lg={4} className="mt-3">
      <Card className="shadow-sm border h-100">
        <Card.Header className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between p-3 border-bottom gap-3">
          <div className="d-flex align-items-center gap-2">
            <div
              className="d-flex align-items-center justify-content-center flex-shrink-0"
              style={{
                width: "30px",
                height: "30px",
                borderRadius: "8px",
                backgroundColor: "rgba(59, 130, 246, 0.12)",
                color: "#3B82F6",
                border: "1px solid rgba(59, 130, 246, 0.25)",
              }}
            >
              <BarChartFill size={15} />
            </div>
            <div>
              <div className="d-flex align-items-center gap-2">
                <h6 className="mb-0 fw-bold" style={{ fontSize: "15px" }}>
                  Payer Financial Comparison
                </h6>
              </div>
              <div className="text-muted" style={{ fontSize: "12px" }}>
                Absolute amounts per payer — how much each one owes, pays and
                rejects (not share of total)
              </div>
            </div>
          </div>
        </Card.Header>

        <Card.Body className="p-4 d-flex flex-column justify-content-between">
          <div style={{ height: "350px", width: "100%", margin: "16px 0" }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={displayData}
                margin={{ top: 10, right: 10, left: -10, bottom: 25 }}
                onClick={(e) => {
                  if (e && e.activePayload && e.activePayload.length > 0) {
                    const clickedPayer = e.activePayload[0].payload.payer;
                    const nextPayer =
                      currentActivePayer === clickedPayer
                        ? "All"
                        : clickedPayer;
                    dispatch(
                      handleClaimDashboardFilter({
                        check: "insurance",
                        value: nextPayer === "All" ? null : nextPayer,
                      }),
                    );
                  }
                }}
                style={{ cursor: "pointer" }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke="rgba(148, 163, 184, 0.15)"
                />
                <XAxis
                  dataKey={isFiltered ? "payer" : "code"}
                  tick={{ fill: "#64748B", fontSize: 11 }}
                  interval={0}
                />
                <YAxis
                  tick={{ fill: "#64748B", fontSize: 11 }}
                  tickFormatter={formatYAxis}
                />
                <Tooltip
                  content={<CustomTooltip />}
                  cursor={{ fill: "rgba(59, 130, 246, 0.08)" }}
                  wrapperStyle={{ zIndex: 1000 }}
                />
                <Legend
                  verticalAlign="top"
                  align="right"
                  wrapperStyle={{ fontSize: "11px", paddingBottom: "10px" }}
                />
                <Bar
                  dataKey="grossAmount"
                  name="Gross"
                  fill="#3B82F6"
                  radius={[4, 4, 0, 0]}
                />
                <Bar
                  dataKey="netSubmitted"
                  name="Net Submitted"
                  fill="#1D4ED8"
                  radius={[4, 4, 0, 0]}
                />
                <Bar
                  dataKey="approvedAmount"
                  name="Approved"
                  fill="#10B981"
                  radius={[4, 4, 0, 0]}
                />
                <Bar
                  dataKey="rejectedAmount"
                  name="Rejected"
                  fill="#EF4444"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card.Body>
      </Card>
    </Col>
  );
};

export default PayerComparisonChart;
